import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Filter, ChevronDown } from "lucide-react";
import { FilterState } from "./CompactFilterBar";
import { FilterContent } from "./FilterContent";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface AdvancedFiltersPanelProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  activeFiltersCount: number;
  // For desktop: controlled externally
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const getResetFilters = (currentFilters: FilterState): FilterState => ({
  ...currentFilters,
  agents: [],
  categories: [],
  riskCategory: 'all' as const,
  severity: 'all' as const,
  retailers: [],
  regions: [],
  riskType: 'all' as const,
  bacteria: [],
  pregnancyOnly: false
});

export const AdvancedFiltersPanel = ({ 
  filters, 
  onFiltersChange,
  isOpen,
  onOpenChange
}: AdvancedFiltersPanelProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isMobile = useIsMobile();

  // Use external control if provided, otherwise internal
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  // Draft state for mobile
  const [draftFilters, setDraftFilters] = useState<FilterState>(filters);

  // Sync draft when panel opens
  useEffect(() => {
    if (open) {
      setDraftFilters(filters);
    }
  }, [open, filters]);

  const handleApply = () => {
    onFiltersChange(draftFilters);
    setOpen(false);
  };

  const handleReset = () => {
    setDraftFilters(getResetFilters(draftFilters));
  };

  const agentsCount = (filters.agents || []).length;
  const advancedActiveCount = [
    agentsCount > 0 && !filters.pregnancyOnly,
    (filters.categories || []).length > 0,
    filters.riskCategory !== 'all',
    filters.severity !== 'all',
    (filters.retailers || []).length > 0,
    filters.regions.length > 0,
    filters.riskType !== 'all',
    filters.bacteria.length > 0
  ].filter(Boolean).length;

  // Mobile: Sheet lateral
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9 relative" aria-label="Filtres avancés">
            <Filter className="h-4 w-4" />
            {advancedActiveCount > 0 && (
              <Badge variant="secondary" className="absolute -top-1 -right-1 px-1.5 py-0 text-xs h-5 min-w-5">
                {advancedActiveCount}
              </Badge>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filtres avancés</SheetTitle>
            <SheetDescription>
              Affinez votre recherche avec des critères détaillés
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6">
            <FilterContent
              filters={draftFilters}
              onFiltersChange={setDraftFilters}
              onApply={handleApply}
              onReset={handleReset}
              layout="mobile"
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Button only (Collapsible rendered in CompactFilterBar)
  return (
    <Button 
      variant="outline" 
      className="h-9 relative gap-2" 
      onClick={() => setOpen(!open)}
      aria-label="Filtres avancés"
    >
      <Filter className="h-4 w-4" />
      <span className="hidden sm:inline">Filtres avancés</span>
      <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", open && "rotate-180")} />
      {advancedActiveCount > 0 && (
        <Badge variant="secondary" className="absolute -top-1 -right-1 px-1.5 py-0 text-xs h-5 min-w-5">
          {advancedActiveCount}
        </Badge>
      )}
    </Button>
  );
};

// Separate component for desktop collapsible panel
interface DesktopFiltersPanelProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  open: boolean;
  onClose: () => void;
}

export const DesktopFiltersPanel = ({
  filters,
  onFiltersChange,
  open,
  onClose
}: DesktopFiltersPanelProps) => {
  // Draft state for desktop
  const [draftFilters, setDraftFilters] = useState<FilterState>(filters);

  // Sync draft when panel opens
  useEffect(() => {
    if (open) {
      setDraftFilters(filters);
    }
  }, [open, filters]);

  const handleApply = () => {
    onFiltersChange(draftFilters);
    onClose();
  };

  const handleReset = () => {
    setDraftFilters(getResetFilters(draftFilters));
  };

  return (
    <Collapsible open={open}>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <div className="border-t mt-3 bg-muted/30 rounded-b-lg max-h-[60vh] overflow-y-auto">
          <FilterContent
            filters={draftFilters}
            onFiltersChange={setDraftFilters}
            onApply={handleApply}
            onReset={handleReset}
            layout="desktop"
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
